import { useState, useEffect, useRef, useCallback } from "react";

export const MAX_NAME_CHARACTERS = 100;
export const MAX_PROMPT_CHARACTERS = 500;

export const SHARED_OPTIONS = {
  ethnicity: [
    "White / European",
    "Greek / Scandinavian",
    "East Asian",
    "South Asian (North Indian)",
    "South Asian (South Indian)",
    "Indian (Northeastern)",
    "African / Black",
    "Hispanic / Latino",
    "Middle Eastern / Arab",
    "Southeast Asian",
    "Central Asian",
    "Indigenous",
    "Mixed / Multiracial",
  ],
  skinTone: [
    "Fair / Porcelain",
    "Light / Warm Beige",
    "Golden Olive",
    "Medium / Honey Tan",
    "Rich Caramel / Warm Brown",
    "Deep Espresso / Radiant Dark",
  ],
  hairColor: [
    "Jet Black",
    "Dark Brown",
    "Light Brown",
    "Blonde",
    "Auburn / Red",
    "Grey / Silver",
  ],
  background: [
    "Clean White Studio",
    "Soft Grey Studio",
    "Warm Beige Studio",
  ],
  age: [
    "18 - 20",
    "Early 20s",
    "Mid 20s",
    "Late 20s",
    "Early 30s",
    "Mid 30s",
    "Late 30s",
    "Early 40s",
    "Mid 40s",
    "Late 40s",
    "50s",
    "60s",
    "70s (up to 80)",
  ],
};

export const FIELD_OPTIONS = {
  female: {
    bodyType: ["Slim", "Athletic / Toned", "Curvy / Hourglass", "Plus Size", "Petite"],
    hairType: ["Straight", "Wavy", "Curly", "Coily / Afro", "Braided"],
    hairLength: ["Pixie / Very Short", "Chin Length", "Shoulder Length", "Mid-Back", "Waist Length"],
    footwear: [
      "Barefoot",
      "Minimalist White Sneakers",
      "Nude Block Heels",
      "Nude Stiletto Heels",
      "Black Ankle Boots",
      "Ballet Flats",
    ],
    clothing: [
      "Plain White Fitted Tee & Slim Blue Jeans",
      "Plain Black Fitted Tee & Black Trousers",
      "Neutral Beige Tank & Straight Trousers",
      "Plain Black Bodysuit & Leggings",
      "Simple Nude Slip Dress",
      "Plain Grey Sports Bra & Leggings",
    ],
  },
  male: {
    bodyType: ["Slim", "Average", "Athletic", "Muscular", "Plus Size / Broad"],
    hairType: ["Straight", "Wavy", "Curly", "Coily / Afro", "Braided / Locs"],
    hairLength: ["Buzz Cut", "Short", "Medium", "Long"],
    footwear: [
      "Barefoot",
      "Minimalist White Sneakers",
      "Black Leather Sneakers",
      "Brown Penny Loafers",
      "Black Formal Oxfords",
      "Brown Chelsea Boots",
    ],
    clothing: [
      "Plain White Tee & Slim Blue Jeans",
      "Plain Black Tee & Black Trousers",
      "Plain Grey Tee & Beige Chinos",
      "Plain White Shirt & Dark Trousers",
      "Plain Grey Sports Tee & Shorts",
      "Plain Black Tank & Joggers",
    ],
  },
};

export const useModelGenerationDrawer = ({
  isOpen,
  onClose,
  onGenerate,
  defaultGender = "",
}) => {
  const [gender, setGender] = useState("");
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [ethnicity, setEthnicity] = useState("");
  const [skinTone, setSkinTone] = useState("");
  const [bodyType, setBodyType] = useState("");
  const [hairType, setHairType] = useState("");
  const [hairLength, setHairLength] = useState("");
  const [hairColor, setHairColor] = useState("");
  const [clothing, setClothing] = useState("");
  const [footwear, setFootwear] = useState("");
  const [background, setBackground] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");

  const [errors, setErrors] = useState({});

  const clearFieldError = useCallback((field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const setFieldError = useCallback((field, msg) => {
    setErrors((prev) => ({ ...prev, [field]: msg }));
  }, []);

  const isMale = gender === "male";
  const genderKey = isMale ? "male" : "female";
  const genderOptions = FIELD_OPTIONS[genderKey];

  // Custom mode toggles
  const [customModes, setCustomModes] = useState({
    age: false,
    ethnicity: false,
    skinTone: false,
    bodyType: false,
    hairType: false,
    hairLength: false,
    hairColor: false,
    clothing: false,
    footwear: false,
    background: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Active open dropdown tracker
  const [openDropdownKey, setOpenDropdownKey] = useState(null);
  const drawerBodyRef = useRef(null);

  const handleReset = useCallback(() => {
    setName("");
    setGender("");
    setAge("");
    setEthnicity("");
    setSkinTone("");
    setBodyType("");
    setHairType("");
    setHairLength("");
    setHairColor("");
    setClothing("");
    setFootwear("");
    setBackground("");
    setCustomPrompt("");
    setErrors({});
    setCustomModes({
      age: false,
      ethnicity: false,
      skinTone: false,
      bodyType: false,
      hairType: false,
      hairLength: false,
      hairColor: false,
      clothing: false,
      footwear: false,
      background: false,
    });
    setOpenDropdownKey(null);
  }, []);

  const isFormValid =
    Boolean(name.trim()) &&
    Boolean(gender) &&
    Boolean(age.trim()) &&
    Boolean(ethnicity.trim()) &&
    Boolean(skinTone.trim()) &&
    Boolean(bodyType.trim()) &&
    Boolean(hairType.trim()) &&
    Boolean(hairLength.trim()) &&
    Boolean(hairColor.trim()) &&
    Boolean(clothing.trim()) &&
    Boolean(footwear.trim()) &&
    Boolean(background.trim()) &&
    Object.keys(errors).length === 0 &&
    !isSubmitting;

  const validateAge = useCallback(
    (val) => {
      const trimmed = String(val ?? "").trim();
      if (!trimmed) {
        setFieldError("age", "Age is required (18 - 80)");
        return false;
      }

      const match = trimmed.match(/\d+/);
      if (match) {
        const num = parseInt(match[0], 10);
        if (num < 18) {
          setFieldError("age", "Age cannot be less than 18");
          return false;
        }
        if (num > 80) {
          setFieldError("age", "Age cannot be more than 80");
          return false;
        }
      } else {
        const lower = trimmed.toLowerCase();
        if (
          lower.includes("child") ||
          lower.includes("kid") ||
          lower.includes("baby") ||
          lower.includes("minor")
        ) {
          setFieldError("age", "Age cannot be less than 18");
          return false;
        }
        if (
          lower.includes("century") ||
          lower.includes("85") ||
          lower.includes("90") ||
          lower.includes("100")
        ) {
          setFieldError("age", "Age cannot be more than 80");
          return false;
        }
      }

      clearFieldError("age");
      return true;
    },
    [clearFieldError, setFieldError]
  );

  const handleAgeChange = useCallback(
    (val) => {
      setAge(val);
      if (customModes.age) {
        validateAge(val);
      } else {
        clearFieldError("age");
      }
    },
    [clearFieldError, customModes.age, validateAge]
  );

  const handleNameChange = useCallback(
    (valOrEvent) => {
      const val = typeof valOrEvent === "string" ? valOrEvent : valOrEvent?.target?.value ?? "";
      setName(val);
      if (errors.name && val.trim()) clearFieldError("name");
    },
    [clearFieldError, errors.name]
  );

  const handleGenderChange = useCallback(
    (newGender) => {
      setGender(newGender);
      clearFieldError("gender");
    },
    [clearFieldError]
  );

  const handleEthnicityChange = useCallback(
    (val) => {
      setEthnicity(val);
      clearFieldError("ethnicity");
    },
    [clearFieldError]
  );

  const handleSkinToneChange = useCallback(
    (val) => {
      setSkinTone(val);
      clearFieldError("skinTone");
    },
    [clearFieldError]
  );

  const handleBodyTypeChange = useCallback(
    (val) => {
      setBodyType(val);
      clearFieldError("bodyType");
    },
    [clearFieldError]
  );

  const handleHairTypeChange = useCallback(
    (val) => {
      setHairType(val);
      clearFieldError("hairType");
    },
    [clearFieldError]
  );

  const handleHairLengthChange = useCallback(
    (val) => {
      setHairLength(val);
      clearFieldError("hairLength");
    },
    [clearFieldError]
  );

  const handleHairColorChange = useCallback(
    (val) => {
      setHairColor(val);
      clearFieldError("hairColor");
    },
    [clearFieldError]
  );

  const handleClothingChange = useCallback(
    (val) => {
      setClothing(val);
      clearFieldError("clothing");
    },
    [clearFieldError]
  );

  const handleFootwearChange = useCallback(
    (val) => {
      setFootwear(val);
      clearFieldError("footwear");
    },
    [clearFieldError]
  );

  const handleBackgroundChange = useCallback(
    (val) => {
      setBackground(val);
      clearFieldError("background");
    },
    [clearFieldError]
  );

  const handleCustomPromptChange = useCallback((valOrEvent) => {
    const val = typeof valOrEvent === "string" ? valOrEvent : valOrEvent?.target?.value ?? "";
    setCustomPrompt(val);
  }, []);

  const toggleCustomMode = useCallback((fieldKey, initialCustomValue) => {
    setCustomModes((prev) => {
      const nextState = !prev[fieldKey];
      if (nextState) {
        const val = initialCustomValue !== undefined ? initialCustomValue : "";
        if (fieldKey === "age") setAge(val);
        else if (fieldKey === "ethnicity") setEthnicity(val);
        else if (fieldKey === "skinTone") setSkinTone(val);
        else if (fieldKey === "bodyType") setBodyType(val);
        else if (fieldKey === "hairType") setHairType(val);
        else if (fieldKey === "hairLength") setHairLength(val);
        else if (fieldKey === "hairColor") setHairColor(val);
        else if (fieldKey === "clothing") setClothing(val);
        else if (fieldKey === "footwear") setFootwear(val);
        else if (fieldKey === "background") setBackground(val);
      }
      return { ...prev, [fieldKey]: nextState };
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      const g =
        defaultGender === "men" || defaultGender === "male"
          ? "male"
          : defaultGender === "women" || defaultGender === "female"
          ? "female"
          : "";
      setGender(g);
      clearFieldError("gender");
    }
  }, [isOpen, defaultGender, clearFieldError]);

  // Close dropdown on outside click or scroll (scrolling inside options list is allowed)
  useEffect(() => {
    if (!openDropdownKey) return;

    const handleClickOutside = (e) => {
      if (!e.target.closest || !e.target.closest(`[data-dropdown-key="${openDropdownKey}"]`)) {
        setOpenDropdownKey(null);
      }
    };

    const handleScroll = (e) => {
      // If the scroll happened inside the dropdown options list itself, keep it open
      if (e.target && e.target.closest && e.target.closest(".model-gen-dropdown-options")) {
        return;
      }
      setOpenDropdownKey(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openDropdownKey]);

  // Escape key close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (openDropdownKey) {
          setOpenDropdownKey(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, openDropdownKey]);

  const handleSubmit = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      const newErrors = {};

      const trimmedName = name.trim();
      if (!trimmedName) {
        newErrors.name = "Model name is required";
      } else if (trimmedName.length > 100) {
        newErrors.name = "Model name must be 100 characters or fewer";
      } else if (/[\/\\:*?"<>|]/.test(trimmedName)) {
        newErrors.name = 'Name cannot contain \\ / : * ? " < > |';
      }

      if (!gender) newErrors.gender = "Gender is required";

      const trimmedAge = String(age || "").trim();
      if (!trimmedAge) {
        newErrors.age = "Age is required (18 - 80)";
      } else {
        const match = trimmedAge.match(/\d+/);
        if (match) {
          const num = parseInt(match[0], 10);
          if (num < 18) newErrors.age = "Age cannot be less than 18";
          else if (num > 80) newErrors.age = "Age cannot be more than 80";
        } else {
          const lower = trimmedAge.toLowerCase();
          if (
            lower.includes("child") ||
            lower.includes("kid") ||
            lower.includes("baby") ||
            lower.includes("minor")
          ) {
            newErrors.age = "Age cannot be less than 18";
          } else if (
            lower.includes("century") ||
            lower.includes("85") ||
            lower.includes("90") ||
            lower.includes("100")
          ) {
            newErrors.age = "Age cannot be more than 80";
          }
        }
      }

      if (!ethnicity.trim()) newErrors.ethnicity = "Ethnicity is required";
      if (!skinTone.trim()) newErrors.skinTone = "Skin tone is required";
      if (!bodyType.trim()) newErrors.bodyType = "Body type is required";
      if (!hairType.trim()) newErrors.hairType = "Hair type is required";
      if (!hairLength.trim()) newErrors.hairLength = "Hair length is required";
      if (!hairColor.trim()) newErrors.hairColor = "Hair color is required";
      if (!clothing.trim()) newErrors.clothing = "Clothing is required";
      if (!footwear.trim()) newErrors.footwear = "Footwear is required";
      if (!background.trim()) newErrors.background = "Studio background is required";

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        const firstKey = Object.keys(newErrors)[0];
        const el =
          document.querySelector(`[data-dropdown-key="${firstKey}"]`) ||
          document.getElementById(`model-gen-${firstKey}`);
        el?.scrollIntoView({ block: "center", behavior: "smooth" });
        return;
      }

      const compiledHair = `${hairLength} ${hairType} (${hairColor})`.trim();

      setIsSubmitting(true);
      try {
        const res = await onGenerate({
          name: trimmedName,
          gender: isMale ? "men" : "women",
          genderRaw: gender,
          age: trimmedAge,
          ethnicity: ethnicity.trim(),
          skinTone: skinTone.trim(),
          bodyType: bodyType.trim(),
          hairType: hairType.trim(),
          hairLength: hairLength.trim(),
          hairColor: hairColor.trim(),
          hair: {
            type: hairType.trim(),
            length: hairLength.trim(),
            color: hairColor.trim(),
          },
          hairString: compiledHair,
          clothing: clothing.trim(),
          footwear: footwear.trim(),
          background: background.trim(),
          customPrompt: customPrompt.trim().slice(0, MAX_PROMPT_CHARACTERS),
        });

        if (res && res.success === false) {
          if (res.status === 409 || (res.message && res.message.toLowerCase().includes("name"))) {
            setErrors((prev) => ({
              ...prev,
              name: res.message || "A model with this name already exists in your library or active queue.",
            }));
            const el = document.getElementById("model-gen-name");
            el?.scrollIntoView({ block: "center", behavior: "smooth" });
          }
          return; // KEEP DRAWER OPEN ON 409 OR ERROR!
        }

        handleReset();
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      age,
      background,
      bodyType,
      clothing,
      customPrompt,
      ethnicity,
      footwear,
      gender,
      hairColor,
      hairLength,
      hairType,
      handleReset,
      isMale,
      name,
      onClose,
      onGenerate,
      skinTone,
    ]
  );

  const isSection1DropdownOpen = openDropdownKey === "age";

  const isSection2DropdownOpen = [
    "ethnicity",
    "skinTone",
    "bodyType",
    "hairType",
    "hairLength",
    "hairColor",
  ].includes(openDropdownKey);

  const isSection3DropdownOpen = [
    "clothing",
    "footwear",
    "background",
  ].includes(openDropdownKey);

  return {
    gender,
    setGender,
    handleGenderChange,
    name,
    setName,
    handleNameChange,
    age,
    setAge,
    handleAgeChange,
    ethnicity,
    setEthnicity,
    handleEthnicityChange,
    skinTone,
    setSkinTone,
    handleSkinToneChange,
    bodyType,
    setBodyType,
    handleBodyTypeChange,
    hairType,
    setHairType,
    handleHairTypeChange,
    hairLength,
    setHairLength,
    handleHairLengthChange,
    hairColor,
    setHairColor,
    handleHairColorChange,
    clothing,
    setClothing,
    handleClothingChange,
    footwear,
    setFootwear,
    handleFootwearChange,
    background,
    setBackground,
    handleBackgroundChange,
    customPrompt,
    setCustomPrompt,
    handleCustomPromptChange,
    errors,
    setErrors,
    clearFieldError,
    setFieldError,
    customModes,
    setCustomModes,
    toggleCustomMode,
    isSubmitting,
    openDropdownKey,
    setOpenDropdownKey,
    drawerBodyRef,
    isFormValid,
    isMale,
    genderKey,
    genderOptions,
    isSection1DropdownOpen,
    isSection2DropdownOpen,
    isSection3DropdownOpen,
    handleReset,
    handleSubmit,
    MAX_NAME_CHARACTERS,
    MAX_PROMPT_CHARACTERS,
    SHARED_OPTIONS,
    FIELD_OPTIONS,
  };
};

export default useModelGenerationDrawer;
